import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { pageTitleFromPath } from '../../utils/enterpriseUi';
import EnterpriseAdminOnboarding from '../../components/enterprise/EnterpriseAdminOnboarding';
import TeamsSetupWizard from '../../components/enterprise/TeamsSetupWizard';
import { HomeSidebar, HomeTopBar } from '../../components/home/HomeHubParts';
import homeStyles from '../../components/home/HomeHub.module.css';
import '../../styles/EnterpriseDashboard.css';
import '../../styles/enterpriseUi.css';

const IconTour = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" />
  </svg>
);

function onboardingDone(payload) {
  const onboarding = payload?.onboarding || payload?.capacity?.onboarding || null;
  return !!(onboarding?.completedAt || onboarding?.skippedAt);
}

export default function EnterpriseAppLayout() {
  const { enterpriseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useToast();
  const { companyById, hasLoaded, refresh } = useWorkspace();
  const [navOpen, setNavOpen] = useState(false);
  const [showTeamsWizard, setShowTeamsWizard] = useState(false);
  const [wizardChecked, setWizardChecked] = useState(false);

  const company = companyById(enterpriseId);

  const entContext = useMemo(() => {
    const finished = !!(company?.onboardingCompletedAt || company?.onboardingDismissedAt);
    return {
      loading: !hasLoaded,
      isAdmin: !!company?.isAdmin,
      companyName: company?.name || '',
      needsOnboarding: !!company?.isAdmin && !finished,
    };
  }, [company, hasLoaded]);

  const handleReplayTour = async () => {
    try {
      await api.patch('/api/enterprise/onboarding', { enterpriseId, action: 'replay' });
      await refresh();
    } catch (err) {
      showError(err.response?.data?.error || 'Could not reset tour');
    }
  };

  useEffect(() => {
    if (!enterpriseId || entContext.loading || !entContext.isAdmin) {
      setShowTeamsWizard(false);
      setWizardChecked(true);
      return undefined;
    }
    let cancelled = false;
    setWizardChecked(false);
    api
      .get('/api/enterprise/capabilities', { params: { enterpriseId } })
      .then((res) => {
        if (cancelled) return;
        const family = res.data?.capacity?.planFamily;
        const done = onboardingDone(res.data);
        setShowTeamsWizard(family === 'teams' && !done);
      })
      .catch(() => {
        if (!cancelled) setShowTeamsWizard(false);
      })
      .finally(() => {
        if (!cancelled) setWizardChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, entContext.loading, entContext.isAdmin]);

  const handleTeamsWizardFinished = async () => {
    setShowTeamsWizard(false);
    try {
      const res = await api.get('/api/enterprise/capabilities', { params: { enterpriseId } });
      const family = res.data?.capacity?.planFamily;
      const done = onboardingDone(res.data);
      setShowTeamsWizard(family === 'teams' && !done);
    } catch {
      // Keep dismissed after a successful skip/complete from the wizard.
    }
    await refresh();
  };

  const showOnboarding =
    enterpriseId
    && !entContext.loading
    && wizardChecked
    && entContext.isAdmin
    && entContext.needsOnboarding
    && !showTeamsWizard;

  return (
    <div className={`${homeStyles.hubRoot} ent-embedded`}>
      <HomeSidebar
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setNavOpen(false);
          navigate(to);
        }}
        footerExtra={
          entContext.isAdmin && !entContext.needsOnboarding && !showTeamsWizard ? (
            <button type="button" className={homeStyles.footerButton} onClick={handleReplayTour}>
              <span className={homeStyles.navIcon} aria-hidden="true">
                <IconTour />
              </span>
              <span className={homeStyles.navLabel}>Enterprise tour</span>
            </button>
          ) : null
        }
      />
      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title={pageTitleFromPath(location.pathname, entContext.companyName || 'Company')}
          onMenu={() => setNavOpen(true)}
          showNotifications={false}
          showThemeToggle
        />
        <div className={`${homeStyles.hubContent} ent-hub-content`}>
          <Outlet />
        </div>
      </div>
      {showTeamsWizard ? (
        <TeamsSetupWizard
          enterpriseId={enterpriseId}
          companyName={entContext.companyName}
          onFinished={handleTeamsWizardFinished}
        />
      ) : showOnboarding ? (
        <EnterpriseAdminOnboarding
          enterpriseId={enterpriseId}
          companyName={entContext.companyName}
          onFinished={refresh}
        />
      ) : null}
    </div>
  );
}
